import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState } from "@/components/States";
import { toast } from "sonner";
import { Plus, X, CalendarDays, Ban, Trash2, Pencil, Building2 } from "lucide-react";

const brl = (n) => Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const input = "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors [color-scheme:dark]";
const DAYS = [["seg", "Seg"], ["ter", "Ter"], ["qua", "Qua"], ["qui", "Qui"], ["sex", "Sex"], ["sab", "Sáb"], ["dom", "Dom"]];
const TYPES = ["Salão de Festas", "Churrasqueira", "Sauna", "Beach Tennis", "Quadra", "Piscina", "Academia", "Espaço Gourmet", "Coworking", "Brinquedoteca", "Playground", "Salão de Jogos", "Pet Place", "Cinema", "Outro"];

function blankSchedule() {
  return Object.fromEntries(DAYS.map(([k]) => [k, { active: true, open: "08:00", close: "22:00" }]));
}
function emptySpace() {
  return {
    name: "", type: "Outro", icon: "🏛️", description: "", location: "", capacity: "",
    usage_mode: "free", reservation_type: "period", slot_minutes: 60,
    periods: [{ label: "", start: "", end: "" }], slot_capacity: 6,
    schedule: blankSchedule(),
    rules: { max_advance_days: "", same_day: true, max_guests: "", limit_per_unit_week: "", limit_per_unit_month: "", cancel_deadline_hours: "" },
    pricing: { mode: "free", fee: "", deposit: "" },
    regulation_text: "", active: true,
  };
}

function SpaceForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setF({ ...f, [k]: v });
  const upRule = (k, v) => setF({ ...f, rules: { ...f.rules, [k]: v } });
  const upPrice = (k, v) => setF({ ...f, pricing: { ...f.pricing, [k]: v } });
  const upDay = (d, k, v) => setF({ ...f, schedule: { ...f.schedule, [d]: { ...f.schedule[d], [k]: v } } });

  const save = async () => {
    if (!f.name.trim()) return toast.error("Informe o nome do espaço.");
    setSaving(true);
    try {
      const payload = {
        ...f,
        capacity: f.capacity ? Number(f.capacity) : null,
        slot_minutes: Number(f.slot_minutes) || 60,
        slot_capacity: Number(f.slot_capacity) || 1,
        periods: (f.periods || []).filter((p) => p.start && p.end),
        rules: {
          ...f.rules,
          max_advance_days: f.rules.max_advance_days ? Number(f.rules.max_advance_days) : null,
          max_guests: f.rules.max_guests === "" ? null : Number(f.rules.max_guests),
          limit_per_unit_week: f.rules.limit_per_unit_week ? Number(f.rules.limit_per_unit_week) : null,
          limit_per_unit_month: f.rules.limit_per_unit_month ? Number(f.rules.limit_per_unit_month) : null,
          cancel_deadline_hours: f.rules.cancel_deadline_hours ? Number(f.rules.cancel_deadline_hours) : null,
        },
        pricing: { mode: f.pricing.mode, fee: Number(f.pricing.fee) || 0, deposit: Number(f.pricing.deposit) || 0 },
      };
      if (f.id) await api.put(`/spaces/${f.id}`, payload);
      else await api.post("/spaces", payload);
      toast.success(f.id ? "Espaço atualizado!" : "Espaço cadastrado!");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const isReservation = f.usage_mode === "reservation";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg glass-strong border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 float-in pb-safe max-h-[90dvh] overflow-y-auto space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-display font-bold text-lg">{f.id ? "Editar espaço" : "Cadastrar espaço"}</p>
          <button onClick={onClose} data-testid="space-form-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <input value={f.icon} onChange={(e) => up("icon", e.target.value)} data-testid="space-icon" placeholder="🎉" className={`${input} text-center`} />
          <input value={f.name} onChange={(e) => up("name", e.target.value)} data-testid="space-name" placeholder="Nome do espaço" className={`${input} col-span-3`} />
        </div>
        <select value={f.type} onChange={(e) => up("type", e.target.value)} data-testid="space-type" className={input}>
          {TYPES.map((t) => <option key={t} className="bg-background">{t}</option>)}
        </select>
        <textarea value={f.description} onChange={(e) => up("description", e.target.value)} data-testid="space-desc" rows={2} placeholder="Descrição" className={`${input} resize-none`} />
        <div className="grid grid-cols-2 gap-2">
          <input value={f.location} onChange={(e) => up("location", e.target.value)} data-testid="space-location" placeholder="Localização" className={input} />
          <input type="number" value={f.capacity} onChange={(e) => up("capacity", e.target.value)} data-testid="space-capacity" placeholder="Capacidade" className={input} />
        </div>

        {/* Como funciona */}
        <div className="rounded-2xl border border-white/10 p-3">
          <p className="text-xs uppercase tracking-wider text-primary font-medium mb-2">Como este espaço funciona?</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => up("usage_mode", "free")} data-testid="mode-free" className={`rounded-xl py-2.5 text-sm font-medium border transition-colors ${!isReservation ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5"}`}>Uso livre</button>
            <button type="button" onClick={() => up("usage_mode", "reservation")} data-testid="mode-reservation" className={`rounded-xl py-2.5 text-sm font-medium border transition-colors ${isReservation ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5"}`}>Necessita de reserva</button>
          </div>
        </div>

        {isReservation && (
          <div className="rounded-2xl border border-white/10 p-3 space-y-3">
            <p className="text-xs uppercase tracking-wider text-primary font-medium">Como pode ser reservado?</p>
            <div className="grid grid-cols-2 gap-2">
              {[["hourly", "Por horário"], ["period", "Por período"], ["full_day", "Dia inteiro"], ["slots", "Por vagas"]].map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => up("reservation_type", k)} data-testid={`rtype-${k}`} className={`rounded-xl py-2 text-xs font-medium border transition-colors ${f.reservation_type === k ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5"}`}>{lbl}</button>
              ))}
            </div>

            {(f.reservation_type === "hourly" || f.reservation_type === "slots") && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Duração (min)</label>
                  <select value={f.slot_minutes} onChange={(e) => up("slot_minutes", e.target.value)} data-testid="slot-minutes" className={input}>
                    {[30, 60, 90, 120].map((m) => <option key={m} value={m} className="bg-background">{m} min</option>)}
                  </select>
                </div>
                {f.reservation_type === "slots" && (
                  <div>
                    <label className="text-xs text-muted-foreground">Vagas por horário</label>
                    <input type="number" value={f.slot_capacity} onChange={(e) => up("slot_capacity", e.target.value)} data-testid="slot-capacity" className={input} />
                  </div>
                )}
              </div>
            )}

            {f.reservation_type === "period" && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Períodos</label>
                {f.periods.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input value={p.label} onChange={(e) => { const n = [...f.periods]; n[i] = { ...p, label: e.target.value }; up("periods", n); }} placeholder="Rótulo" className={`${input} col-span-5`} data-testid="period-label" />
                    <input type="time" value={p.start} onChange={(e) => { const n = [...f.periods]; n[i] = { ...p, start: e.target.value }; up("periods", n); }} className={`${input} col-span-3`} data-testid="period-start" />
                    <input type="time" value={p.end} onChange={(e) => { const n = [...f.periods]; n[i] = { ...p, end: e.target.value }; up("periods", n); }} className={`${input} col-span-3`} data-testid="period-end" />
                    <button type="button" onClick={() => up("periods", f.periods.filter((_, j) => j !== i))} className="col-span-1 text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => up("periods", [...f.periods, { label: "", start: "", end: "" }])} className="text-xs text-primary flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Adicionar período</button>
              </div>
            )}

            {/* Regras */}
            <p className="text-xs uppercase tracking-wider text-primary font-medium pt-1">Regras de reserva</p>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-muted-foreground">Antecedência máx. (dias)</label><input type="number" value={f.rules.max_advance_days} onChange={(e) => upRule("max_advance_days", e.target.value)} className={input} data-testid="rule-maxadv" /></div>
              <div><label className="text-xs text-muted-foreground">Máx. convidados</label><input type="number" value={f.rules.max_guests} onChange={(e) => upRule("max_guests", e.target.value)} className={input} data-testid="rule-guests" /></div>
              <div><label className="text-xs text-muted-foreground">Limite/unidade semana</label><input type="number" value={f.rules.limit_per_unit_week} onChange={(e) => upRule("limit_per_unit_week", e.target.value)} className={input} /></div>
              <div><label className="text-xs text-muted-foreground">Cancel. até (horas)</label><input type="number" value={f.rules.cancel_deadline_hours} onChange={(e) => upRule("cancel_deadline_hours", e.target.value)} className={input} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.rules.same_day} onChange={(e) => upRule("same_day", e.target.checked)} className="accent-purple-500" data-testid="rule-sameday" /> Permitir reserva no mesmo dia</label>

            {/* Valores */}
            <p className="text-xs uppercase tracking-wider text-primary font-medium pt-1">Valores</p>
            <div className="grid grid-cols-3 gap-2">
              <select value={f.pricing.mode} onChange={(e) => upPrice("mode", e.target.value)} data-testid="price-mode" className={input}>
                <option value="free" className="bg-background">Gratuito</option>
                <option value="paid" className="bg-background">Pago</option>
              </select>
              {f.pricing.mode === "paid" && (
                <>
                  <input type="number" value={f.pricing.fee} onChange={(e) => upPrice("fee", e.target.value)} placeholder="Taxa R$" className={input} data-testid="price-fee" />
                  <input type="number" value={f.pricing.deposit} onChange={(e) => upPrice("deposit", e.target.value)} placeholder="Caução R$" className={input} data-testid="price-deposit" />
                </>
              )}
            </div>
          </div>
        )}

        {/* Dias e horários */}
        <div className="rounded-2xl border border-white/10 p-3 space-y-2">
          <p className="text-xs uppercase tracking-wider text-primary font-medium">Dias e horários</p>
          {DAYS.map(([k, lbl]) => (
            <div key={k} className="flex items-center gap-2">
              <button type="button" onClick={() => upDay(k, "active", !f.schedule[k].active)} data-testid={`day-${k}`} className={`w-14 shrink-0 rounded-lg py-1.5 text-xs font-medium border transition-colors ${f.schedule[k].active ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5 text-muted-foreground"}`}>{lbl}</button>
              <input type="time" value={f.schedule[k].open} onChange={(e) => upDay(k, "open", e.target.value)} disabled={!f.schedule[k].active} className={`${input} flex-1 disabled:opacity-40`} />
              <input type="time" value={f.schedule[k].close} onChange={(e) => upDay(k, "close", e.target.value)} disabled={!f.schedule[k].active} className={`${input} flex-1 disabled:opacity-40`} />
            </div>
          ))}
        </div>

        <textarea value={f.regulation_text} onChange={(e) => up("regulation_text", e.target.value)} data-testid="space-regulation" rows={3} placeholder="Regulamento / regras em texto (também alimenta o Pergunte ao KONDDO)" className={`${input} resize-none`} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => up("active", e.target.checked)} className="accent-purple-500" data-testid="space-active" /> Espaço ativo</label>

        <button onClick={save} disabled={saving} data-testid="space-save" className="w-full rounded-full brand-gradient py-3 text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">{saving ? "Salvando..." : (f.id ? "Salvar alterações" : "Cadastrar espaço")}</button>
      </div>
    </div>
  );
}

function Spaces() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-spaces"], queryFn: async () => (await api.get("/spaces")).data });
  const [form, setForm] = useState(null);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["mgr-spaces"] }); setForm(null); };
  const del = async (id) => { try { await api.delete(`/spaces/${id}`); qc.invalidateQueries({ queryKey: ["mgr-spaces"] }); toast.success("Espaço removido."); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4">
      <button onClick={() => setForm(emptySpace())} data-testid="add-space-btn" className="w-full rounded-3xl brand-gradient p-4 text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform"><Plus className="w-5 h-5" /> Cadastrar espaço</button>
      {isLoading ? <Loading /> : !data?.length ? <EmptyState icon={Building2} title="Nenhum espaço cadastrado" subtitle="Cadastre as áreas comuns do condomínio." /> : (
        <div className="grid gap-3">
          {data.map((s) => (
            <div key={s.id} data-testid="mgr-space" className="glass rounded-3xl p-4 flex items-center gap-3">
              <span className="text-2xl">{s.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-sm">{s.name} {!s.active && <span className="text-xs text-muted-foreground">(inativo)</span>}</p>
                <p className="text-xs text-muted-foreground">
                  {s.usage_mode === "free" ? "Uso livre" : `Reserva · ${s.reservation_type}`}
                  {s.pricing?.mode === "paid" ? ` · ${brl(s.pricing.fee)}` : ""}
                </p>
              </div>
              <button onClick={() => setForm(s)} data-testid="edit-space" className="p-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(s.id)} data-testid="delete-space" className="p-2 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      {form && <SpaceForm initial={form} onClose={() => setForm(null)} onSaved={refresh} />}
    </div>
  );
}

function ReservationsList() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-reservations"], queryFn: async () => (await api.get("/reservations")).data });
  const [filter, setFilter] = useState("todas");
  const cancel = async (id) => { try { await api.post(`/reservations/${id}/cancel`); qc.invalidateQueries({ queryKey: ["mgr-reservations"] }); toast.success("Reserva cancelada."); } catch (e) { toast.error(apiError(e)); } };

  if (isLoading) return <Loading />;
  const today = new Date().toISOString().slice(0, 10);
  let items = data || [];
  if (filter === "hoje") items = items.filter((r) => r.date === today);
  else if (filter === "confirmadas") items = items.filter((r) => r.status === "confirmada");
  else if (filter === "canceladas") items = items.filter((r) => r.status === "cancelada");

  const STATUS = { pendente: "text-yellow-400", confirmada: "text-emerald-400", cancelada: "text-red-400", recusada: "text-red-400" };
  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[["todas", "Todas"], ["hoje", "Hoje"], ["confirmadas", "Confirmadas"], ["canceladas", "Canceladas"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`resf-${k}`} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === k ? "brand-gradient text-white" : "bg-white/5 border border-white/10 text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      {!items.length ? <EmptyState icon={CalendarDays} title="Nenhuma reserva" /> : items.map((r) => (
        <div key={r.id} data-testid="mgr-reservation" className="glass rounded-2xl p-4 flex items-center gap-3">
          <span className="text-xl">{r.space_icon}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{r.space_name} <span className="text-xs text-muted-foreground">· {r.slot_label}</span></p>
            <p className="text-xs text-muted-foreground">{r.resident_name} · {r.unit} · {new Date(r.date).toLocaleDateString("pt-BR")}{r.guests ? ` · ${r.guests} conv.` : ""}{r.value ? ` · ${brl(r.value)}` : ""}</p>
          </div>
          <span className={`text-xs capitalize ${STATUS[r.status]}`}>{r.status}</span>
          {r.status === "confirmada" && <button onClick={() => cancel(r.id)} data-testid="mgr-cancel-res" className="p-2 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"><Ban className="w-4 h-4" /></button>}
        </div>
      ))}
    </div>
  );
}

function CalendarView() {
  const { data: spaces } = useQuery({ queryKey: ["mgr-spaces"], queryFn: async () => (await api.get("/spaces")).data });
  const { data: reservations, isLoading } = useQuery({ queryKey: ["mgr-reservations"], queryFn: async () => (await api.get("/reservations")).data });
  const [spaceId, setSpaceId] = useState("all");
  const reservable = (spaces || []).filter((s) => s.reservable);

  if (isLoading) return <Loading />;
  let items = (reservations || []).filter((r) => r.status === "confirmada");
  if (spaceId !== "all") items = items.filter((r) => r.space_id === spaceId);

  const byDate = {};
  items.forEach((r) => { (byDate[r.date] = byDate[r.date] || []).push(r); });
  const dates = Object.keys(byDate).sort();

  return (
    <div className="space-y-3">
      <select value={spaceId} onChange={(e) => setSpaceId(e.target.value)} data-testid="cal-space-filter" className={input}>
        <option value="all" className="bg-background">Todos os espaços reserváveis</option>
        {reservable.map((s) => <option key={s.id} value={s.id} className="bg-background">{s.name}</option>)}
      </select>
      <p className="text-xs text-muted-foreground">Áreas de uso livre não ocupam o calendário de reservas.</p>
      {!dates.length ? <EmptyState icon={CalendarDays} title="Sem reservas no calendário" /> : dates.map((d) => (
        <div key={d} className="glass rounded-2xl p-4">
          <p className="text-sm font-semibold mb-2">{new Date(d).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}</p>
          <div className="space-y-1.5">
            {byDate[d].map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-xs rounded-lg bg-white/5 px-3 py-2">
                <span>{r.space_icon}</span><span className="font-medium">{r.space_name}</span>
                <span className="text-muted-foreground">· {r.slot_label} · {r.unit}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const SUB = [["espacos", "Espaços"], ["reservas", "Reservas"], ["calendario", "Calendário"]];

export default function ReservationsCenter() {
  const [sub, setSub] = useState("espacos");
  return (
    <div className="space-y-4">
      <div className="flex gap-2 glass rounded-full p-1 max-w-md">
        {SUB.map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`rc-${k}`} className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${sub === k ? "brand-gradient text-white" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      {sub === "espacos" && <Spaces />}
      {sub === "reservas" && <ReservationsList />}
      {sub === "calendario" && <CalendarView />}
    </div>
  );
}
